import React, { useCallback, useState } from 'react';

import { CrossDockPicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/CrossDockPicker';
import { VendorPicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/VendorPicker';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Switch } from 'shadcn/components/ui/Switch';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: any) => any) => void} props.onDraftChange
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.primaryErrors]
 * @param {Object} [props.primaryFieldErrors]
 */
export function PackagingPOCreatePrimaryCard({
    draft,
    onDraftChange,
    primaryErrors = [],
    primaryFieldErrors = {},
    showValidation = false,
}) {
    const [showCrossDockFields, setShowCrossDockFields] = useState(false);

    const setDraftField = useCallback(
        (field, value) => {
            onDraftChange((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [onDraftChange],
    );

    const crossDockEnabled = showCrossDockFields || Boolean(draft.crossDockId);

    const handleCrossDockSwitchChange = useCallback(
        (checked) => {
            if (checked) {
                setShowCrossDockFields(true);
            } else {
                setShowCrossDockFields(false);
                setDraftField('crossDockId', '');
            }
        },
        [setDraftField],
    );

    const handleInputChange = useCallback(
        (field) => (event) => {
            const value = event?.target?.value ?? '';
            setDraftField(field, value);
            if (field === 'scheduledDeliveryDate' && value && !draft.scheduledTime) {
                setDraftField('scheduledTime', '00:00');
            }
        },
        [draft.scheduledTime, setDraftField],
    );

    const showErrors = showValidation === true;
    const getFieldError = useCallback(
        (field) => {
            if (!showErrors) return '';
            return primaryFieldErrors?.[field] ?? '';
        },
        [primaryFieldErrors, showErrors],
    );
    const renderFieldError = useCallback(
        (field) => {
            const message = getFieldError(field);
            if (!message) return null;
            return <p className="text-xs text-destructive">{message}</p>;
        },
        [getFieldError],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Purchase Order Information</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showErrors && primaryErrors.length > 0 ? (
                    <div className="pb-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following to save this purchase order</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {primaryErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-12">
                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <VendorPicker
                            value={draft.vendorId}
                            onValueChange={(id) => setDraftField('vendorId', id)}
                            label={
                                <>
                                    Vendor <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {renderFieldError('vendorId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <FacilityPickerV2
                            label={
                                <>
                                    Facility <span className="text-destructive">*</span>
                                </>
                            }
                            value={draft.facilities}
                            onValueChange={(facilities) => setDraftField('facilities', facilities)}
                            multiSelect
                        />
                        {renderFieldError('facilityId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                            Scheduled Delivery Date <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="scheduledDeliveryDate"
                            type="date"
                            value={draft.scheduledDeliveryDate}
                            onChange={handleInputChange('scheduledDeliveryDate')}
                        />
                        {renderFieldError('scheduledDeliveryDate')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="scheduledDeliveryTime" className={labelClassName}>
                            Scheduled Delivery Time
                        </Label>
                        <Input
                            id="scheduledDeliveryTime"
                            type="time"
                            step="60"
                            value={draft.scheduledTime}
                            onChange={handleInputChange('scheduledTime')}
                        />
                        {renderFieldError('scheduledTime')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="referenceNumber" className={labelClassName}>
                            Reference Number
                        </Label>
                        <Input
                            id="referenceNumber"
                            type="text"
                            value={draft.referenceNumber}
                            onChange={handleInputChange('referenceNumber')}
                        />
                        {renderFieldError('referenceNumber')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <div className="w-full max-w-md">
                            <div className="flex items-center justify-between gap-3 rounded-md border border-input bg-card px-3 py-2">
                                <span className="text-sm text-foreground">Ship to cross dock/offsite?</span>
                                <Switch
                                    checked={crossDockEnabled}
                                    onCheckedChange={handleCrossDockSwitchChange}
                                    aria-label="Ship to cross dock/offsite"
                                />
                            </div>
                        </div>
                        {crossDockEnabled ? (
                            <div className="flex flex-wrap items-end gap-3 pt-2">
                                <CrossDockPicker
                                    value={draft.crossDockId}
                                    onValueChange={(id) => setDraftField('crossDockId', id)}
                                    label="Cross Dock/Offsite Location"
                                />
                                {renderFieldError('crossDockId')}
                            </div>
                        ) : null}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

PackagingPOCreatePrimaryCard.displayName = 'PackagingPOCreatePrimaryCard';
